"use client";

import { c as _c } from "react/compiler-runtime";
import { FORM_ATTACHMENT_PART_PREFIX, FORM_HONEYPOT_FIELD, validateFormSubmission } from "@multi-tenant-cms/types/forms";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { buildFormDesignScopedCss, formDesignClassNames, parseFormDesignJson, resolveFormDesign } from "./formDesign";
import { focusFirstInvalidField } from "./formFieldErrorStore";
import { extractFieldErrorsFromResponse, parseFormSchemaJson, validationErrorCount, validationSummaryMessage } from "./formPublicValidation";
import { FormValidationProvider, useFormValidation } from "./formPublicValidationContext";
import { flushVisitorIntelligenceTracker, formJourneyEventData, trackFormViewOnce, trackVisitorIntelligenceEvent, VISITOR_INTELLIGENCE_PUBLIC_BOOTSTRAP_EVENT } from "./visitorIntelligencePublicTrack";
type GrecaptchaWindow = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: {
    action: string;
  }) => Promise<string>;
};
const RECAPTCHA_SCRIPT_ID = "google-recaptcha-v3";
const RECAPTCHA_READY_TIMEOUT_MS = 12_000;
const DEFAULT_PUBLIC_FORM_SUBMIT_PATH = "/api/forms/submit";
function resolvePublicFormSubmitPath(explicit?: string): string {
  const trimmed = explicit?.trim();
  if (trimmed && trimmed.startsWith("/")) {
    return trimmed;
  }
  return DEFAULT_PUBLIC_FORM_SUBMIT_PATH;
}
function getGrecaptchaApi(): GrecaptchaWindow | null {
  if (typeof window === "undefined") {
    return null;
  }
  const g = (window as unknown as {
    grecaptcha?: GrecaptchaWindow;
  }).grecaptcha;
  if (g && typeof g.ready === "function" && typeof g.execute === "function") {
    return g;
  }
  return null;
}

/** Wait until Google attaches `grecaptcha.ready` + `execute` (often after script `onload`). */
function waitForGrecaptchaApi(timeoutMs = RECAPTCHA_READY_TIMEOUT_MS): Promise<GrecaptchaWindow> {
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const tick = () => {
      const g = getGrecaptchaApi();
      if (g) {
        resolve(g);
        return;
      }
      if (Date.now() - t0 > timeoutMs) {
        reject(new Error("reCAPTCHA did not finish loading. Confirm the site key is a v3 key and this domain is allowed in the Google reCAPTCHA admin console."));
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}
function loadRecaptchaV3(siteKey: string): Promise<GrecaptchaWindow> {
  if (typeof window === "undefined" || !siteKey.trim()) {
    return Promise.reject(new Error("reCAPTCHA site key is missing."));
  }
  const already = getGrecaptchaApi();
  if (already) {
    return Promise.resolve(already);
  }
  return new Promise((resolve, reject) => {
    const finish = () => {
      waitForGrecaptchaApi().then(resolve).catch(reject);
    };
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing?.src?.includes("recaptcha/api.js")) {
      finish();
      return;
    }
    const s = document.createElement("script");
    s.id = RECAPTCHA_SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.onload = () => finish();
    s.onerror = () => reject(new Error("reCAPTCHA script failed to load. Check network, ad blockers, and that google.com/recaptcha is allowed."));
    document.head.appendChild(s);
  });
}
async function getRecaptchaV3Token(siteKey: string): Promise<string> {
  const g = await loadRecaptchaV3(siteKey);
  return new Promise((resolve, reject) => {
    g.ready(() => {
      g.execute(siteKey, {
        action: "form_submit"
      }).then(token => {
        if (!token?.trim()) {
          reject(new Error("reCAPTCHA returned an empty token. Check the site key and allowed domains."));
          return;
        }
        resolve(token);
      }).catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "reCAPTCHA execute failed for this domain.";
        reject(new Error(msg));
      });
    });
  });
}

// An empty `<input type=file>` still posts a nameless 0-byte File.
function chosenFiles(values: FormDataEntryValue[]): File[] {
  return values.filter((v): v is File => typeof v !== "string" && (v.name !== "" || v.size > 0));
}
function formDataToRecord(fd: FormData): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of new Set([...fd.keys()])) {
    const all = fd.getAll(key);
    if (all.some(v => typeof v !== "string")) {
      // File fields validate on name + size; the bytes travel as multipart parts.
      out[key] = chosenFiles(all).map(f => ({
        name: f.name,
        size: f.size
      }));
    } else if (all.length > 1) {
      out[key] = all.map(v => typeof v === "string" ? v : String(v));
    } else {
      const v = all[0];
      out[key] = typeof v === "string" ? v : v == null ? "" : String(v);
    }
  }
  return out;
}

// JSON unless files were chosen; then multipart with `payload` + `file:<field>` parts.
function submitRequestInit(fd: FormData, payload: string): RequestInit {
  const body = new FormData();
  let hasFiles = false;
  for (const key of new Set([...fd.keys()])) {
    for (const file of chosenFiles(fd.getAll(key))) {
      body.append(`${FORM_ATTACHMENT_PART_PREFIX}${key}`, file, file.name);
      hasFiles = true;
    }
  }
  if (!hasFiles) {
    return {
      headers: {
        "Content-Type": "application/json"
      },
      body: payload
    };
  }
  body.append("payload", payload);
  return {
    body
  };
}
type ConfirmJson = {
  kind: "message" | "redirect";
  message?: string;
  redirectUrl?: string;
};
type VisitorIntelligenceWindow = Window & {
  __CMS_VISITOR_INTELLIGENCE__?: {
    allowed?: boolean;
    sessionId?: string | null;
    visitorId?: string | null;
  };
};
function getVisitorIntelligencePayload(): {
  occurredAt: string;
  sessionId?: string;
  visitorId: string;
} | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const state = (window as VisitorIntelligenceWindow).__CMS_VISITOR_INTELLIGENCE__;
  if (!(state?.allowed && state.visitorId)) {
    return undefined;
  }
  return {
    occurredAt: new Date().toISOString(),
    ...(state.sessionId ? {
      sessionId: state.sessionId
    } : {}),
    visitorId: state.visitorId
  };
}
const DESTRUCTIVE_COLOR = "var(--destructive, #b91c1c)";
function PublicFormAlerts(t0) {
  const $ = _c(8);
  const {
    error,
    validationSummary
  } = t0;
  if (!(error || validationSummary)) {
    return null;
  }
  let t1;
  if ($[0] !== validationSummary) {
    t1 = validationSummary ? <p role="alert" style={{
      color: DESTRUCTIVE_COLOR,
      fontSize: 14,
      marginTop: 8
    }}>{validationSummary}</p> : null;
    $[0] = validationSummary;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== error || $[3] !== validationSummary) {
    t2 = error ? <p role="alert" style={{
      color: DESTRUCTIVE_COLOR,
      fontSize: 14,
      marginTop: validationSummary ? 4 : 8
    }}>{error}</p> : null;
    $[2] = error;
    $[3] = validationSummary;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function PublicFormRootInner({
  nodeId,
  style,
  formDbId,
  formSlug,
  formSubmitPath,
  formSchemaJson,
  formDesignJson,
  actionUrl,
  method,
  successMessageClient,
  recaptchaEnabled,
  recaptchaSiteKey,
  htmlClassName,
  htmlId,
  children
}: {
  nodeId: string;
  style: CSSProperties;
  formDbId?: string;
  /** Published form slug (non-PII) for Visitor Intelligence form_* events. */
  formSlug?: string;
  /** Region-prefixed submit path when AFD path routing is active (e.g. `/us-uk/api/forms/submit`). */
  formSubmitPath?: string;
  /** Serialized `form_root` element tree for client-side validation. */
  formSchemaJson?: string;
  /** Serialized `form_root.design` JSON. */
  formDesignJson?: string;
  actionUrl?: string;
  method?: string;
  successMessageClient?: string;
  recaptchaEnabled?: boolean;
  recaptchaSiteKey?: string;
  htmlClassName?: string;
  htmlId?: string;
  children: ReactNode;
}) {
  const [done, setDone] = useState<ConfirmJson | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationSummary, setValidationSummary] = useState<string | null>(null);
  const validation = useFormValidation();
  const schemaRoot = parseFormSchemaJson(formSchemaJson);
  const design = resolveFormDesign(parseFormDesignJson(formDesignJson));
  const designClass = formDesignClassNames(nodeId, design);
  const designCss = buildFormDesignScopedCss(nodeId, design);
  const mergedClassName = [designClass, htmlClassName?.trim()].filter(Boolean).join(" ");
  /** Prevents double POST (e.g. double-click) which would create duplicate HubSpot rows. */
  const submitInFlightRef = useRef(false);
  const formStartedRef = useRef(false);
  const formSubmittedRef = useRef(false);
  const m = (method || "post").toLowerCase() === "get" ? "get" : "post";
  const siteKey = recaptchaSiteKey?.trim() ?? "";
  const shouldLoadRecaptcha = Boolean(recaptchaEnabled && siteKey);
  const journeyFormId = formDbId?.trim() || "";
  const journeyFormSlug = formSlug?.trim() || "";
  const redirectTo = done?.kind === "redirect" ? done.redirectUrl : null;
  useEffect(() => {
    if (redirectTo) {
      window.location.assign(redirectTo);
    }
  }, [redirectTo]);

  /** Prefetch reCAPTCHA v3 on mount so the badge appears before submit (not only after a failed attempt). */
  useEffect(() => {
    if (!shouldLoadRecaptcha) {
      return;
    }
    loadRecaptchaV3(siteKey).catch(() => {
      // Leave errors for submit; preload is best-effort.
    });
  }, [shouldLoadRecaptcha, siteKey]);
  useEffect(() => {
    if (!journeyFormId) {
      return;
    }
    const tryTrackView = () => {
      trackFormViewOnce({
        formId: journeyFormId,
        formSlug: journeyFormSlug
      });
    };
    tryTrackView();
    window.addEventListener(VISITOR_INTELLIGENCE_PUBLIC_BOOTSTRAP_EVENT, tryTrackView);
    return () => {
      window.removeEventListener(VISITOR_INTELLIGENCE_PUBLIC_BOOTSTRAP_EVENT, tryTrackView);
    };
  }, [journeyFormId, journeyFormSlug]);
  useEffect(() => {
    if (!journeyFormId) {
      return;
    }
    const onPageHide = () => {
      if (!(formStartedRef.current && !formSubmittedRef.current)) {
        return;
      }
      trackVisitorIntelligenceEvent("form_abandon", formJourneyEventData({
        formId: journeyFormId,
        formSlug: journeyFormSlug
      }));
    };
    // Capture so abandon is queued before the Region bootstrap flushes on pagehide.
    window.addEventListener("pagehide", onPageHide, true);
    return () => {
      window.removeEventListener("pagehide", onPageHide, true);
    };
  }, [journeyFormId, journeyFormSlug]);
  if (done?.kind === "redirect" && done.redirectUrl) {
    return <div key={nodeId} role="status" style={style}>
				<p style={{
        color: "var(--tt-color-muted-foreground, #6b7280)",
        fontSize: 14,
        margin: 0
      }}>
					Redirecting…
				</p>
			</div>;
  }
  if (done?.kind === "message") {
    return <div key={nodeId} role="status" style={style}>
				<p style={{
        margin: 0
      }}>
					{done.message || successMessageClient || "Thanks — your submission was received."}
				</p>
			</div>;
  }
  const applyFieldValidationErrors = (errors: Record<string, string>) => {
    validation?.setFieldErrors(errors);
    setValidationSummary(validationSummaryMessage(errors));
    setError(null);
    requestAnimationFrame(() => {
      focusFirstInvalidField(errors);
    });
  };
  return <>
			<style dangerouslySetInnerHTML={{
      __html: designCss
    }} key={`${nodeId}-pb-form-design-css`} />
			<form action={formDbId ? undefined : actionUrl?.trim() || undefined} aria-label="Form" className={mergedClassName || undefined} data-pb-node={nodeId} id={htmlId} key={nodeId} method={formDbId ? m : m} noValidate={!!formDbId} onFocus={() => {
      if (!journeyFormId || formStartedRef.current) {
        return;
      }
      formStartedRef.current = true;
      trackVisitorIntelligenceEvent("form_start", formJourneyEventData({
        formId: journeyFormId,
        formSlug: journeyFormSlug
      }));
    }} onSubmit={async e => {
      if (!formDbId) {
        if (actionUrl?.trim()) {
          return;
        }
        e.preventDefault();
        setError("Form is not configured for submission.");
        return;
      }
      e.preventDefault();
      if (submitInFlightRef.current) {
        return;
      }
      submitInFlightRef.current = true;
      setError(null);
      setValidationSummary(null);
      validation?.clearAllFieldErrors();
      const fd = new FormData(e.currentTarget);
      const data = formDataToRecord(fd);
      if (schemaRoot) {
        const clientResult = validateFormSubmission(schemaRoot, data);
        if (clientResult.valid === false) {
          submitInFlightRef.current = false;
          applyFieldValidationErrors(clientResult.errors);
          return;
        }
      }
      const ref = typeof window === "undefined" ? undefined : window.location.href;
      const visitorIntelligence = getVisitorIntelligencePayload();
      try {
        let recaptchaToken: string | undefined;
        if (shouldLoadRecaptcha) {
          try {
            recaptchaToken = await getRecaptchaV3Token(siteKey);
          } catch (err) {
            submitInFlightRef.current = false;
            setError(err instanceof Error ? err.message : "reCAPTCHA could not run in this page.");
            return;
          }
        }
        const payload = JSON.stringify({
          formId: formDbId,
          data,
          referrerUrl: ref,
          // For the {{pageTitle}} merge tag in notification emails.
          pageTitle: document.title.slice(0, 300),
          ...(visitorIntelligence ? {
            visitorIntelligence
          } : {}),
          ...(recaptchaToken ? {
            recaptchaToken
          } : {})
        });
        const res = await fetch(resolvePublicFormSubmitPath(formSubmitPath), {
          method: "POST",
          ...submitRequestInit(fd, payload)
        });
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          errors?: Record<string, string>;
          fieldErrors?: Record<string, string>;
          confirmation?: ConfirmJson;
          submissionId?: string;
        };
        if (!res.ok) {
          submitInFlightRef.current = false;
          const fieldErrors = extractFieldErrorsFromResponse(body);
          if (fieldErrors && validationErrorCount(fieldErrors) > 0) {
            applyFieldValidationErrors(fieldErrors);
            return;
          }
          setError(body.error || "Submission failed.");
          return;
        }
        formSubmittedRef.current = true;
        if (journeyFormId) {
          const submissionId = typeof body.submissionId === "string" ? body.submissionId.trim() : "";
          trackVisitorIntelligenceEvent("form_submit", formJourneyEventData({
            formId: journeyFormId,
            formSlug: journeyFormSlug,
            submissionId
          }));
          flushVisitorIntelligenceTracker({
            keepalive: true
          });
        }
        const c = body.confirmation;
        if (c?.kind === "redirect" && c.redirectUrl) {
          setDone(c);
          return;
        }
        if (c?.kind === "message" && c.message) {
          setDone(c);
          return;
        }
        setDone({
          kind: "message",
          message: successMessageClient
        });
      } catch {
        submitInFlightRef.current = false;
        setError("Network error — try again later.");
      }
    }} style={style}>
				{design.heading ? <h2 className="pb-form-heading">{design.heading}</h2> : null}
				{design.subtitle ? <p className="pb-form-subtitle">{design.subtitle}</p> : null}
				{children}
				{formDbId ?
      // Honeypot: off-screen (not display:none) so naive bots still fill it.
      <input aria-hidden="true" autoComplete="off" defaultValue="" name={FORM_HONEYPOT_FIELD} style={{
        position: "absolute",
        left: "-10000px",
        width: 1,
        height: 1,
        overflow: "hidden"
      }} tabIndex={-1} type="text" /> : null}
				{design.disclaimer ? <p className="pb-form-disclaimer">{design.disclaimer}</p> : null}
				<PublicFormAlerts error={error} validationSummary={validationSummary} />
			</form>
		</>;
}
export function PublicFormRoot(t0) {
  const $ = _c(19);
  const {
    nodeId,
    style,
    formDbId,
    formSlug,
    formSubmitPath,
    formSchemaJson,
    formDesignJson,
    actionUrl,
    method,
    successMessageClient,
    recaptchaEnabled,
    recaptchaSiteKey,
    htmlClassName,
    htmlId,
    children
  } = t0;
  let t1;
  if ($[0] !== actionUrl || $[1] !== children || $[2] !== formDbId || $[3] !== formDesignJson || $[4] !== formSchemaJson || $[5] !== formSlug || $[6] !== formSubmitPath || $[7] !== htmlClassName || $[8] !== htmlId || $[9] !== method || $[10] !== nodeId || $[11] !== recaptchaEnabled || $[12] !== recaptchaSiteKey || $[13] !== style || $[14] !== successMessageClient) {
    t1 = <PublicFormRootInner actionUrl={actionUrl} formDbId={formDbId} formDesignJson={formDesignJson} formSchemaJson={formSchemaJson} formSlug={formSlug} formSubmitPath={formSubmitPath} htmlClassName={htmlClassName} htmlId={htmlId} method={method} nodeId={nodeId} recaptchaEnabled={recaptchaEnabled} recaptchaSiteKey={recaptchaSiteKey} style={style} successMessageClient={successMessageClient}>{children}</PublicFormRootInner>;
    $[0] = actionUrl;
    $[1] = children;
    $[2] = formDbId;
    $[3] = formDesignJson;
    $[4] = formSchemaJson;
    $[5] = formSlug;
    $[6] = formSubmitPath;
    $[7] = htmlClassName;
    $[8] = htmlId;
    $[9] = method;
    $[10] = nodeId;
    $[11] = recaptchaEnabled;
    $[12] = recaptchaSiteKey;
    $[13] = style;
    $[14] = successMessageClient;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  let t2;
  if ($[16] !== nodeId || $[17] !== t1) {
    t2 = <FormValidationProvider formInstanceId={nodeId}>{t1}</FormValidationProvider>;
    $[16] = nodeId;
    $[17] = t1;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  return t2;
}